import React, { useState, useEffect } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import Select from "react-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, Pill, History, Save } from "lucide-react";

const UPDATE_PARTICIPANT = gql`
  mutation UpdateParticipantByODRNumber($odrnumber: Int!, $participant: Int, $updates: UpdateParticipantInput!) {
    updateParticipantByODRNumber(odrnumber: $odrnumber, participant: $participant, updates: $updates) {
      message
    }
  }
`;

const GET_ICD10_CODES = gql`
  query GetICD10Codes {
    getICD10Codes {
      icd10
      icd10Description
    }
  }
`;

interface ICD10Option {
  value: string;
  label: string;
}

interface MedicalData {
  icd10?: string;
  psychotropicMeds?: string;
  otherMeds?: string;
  currentMeds?: string;
  clinicalPresentation?: string;
  psychHistory?: string;
  medicalProblems?: string;
  substanceHistory?: string;
  dsm5dx?: string;
}

interface FormData {
  icd10Codes: ICD10Option[];
  psychotropicMeds: string;
  otherMeds: string;
  clinicalPresentation: string;
  psychHistory: string;
  medicalProblems: string;
  substanceHistory: string;
  dsm5dx: string;
  currentMeds: string;
}

interface EditMedicalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  programNumber: number;
  medicalData: MedicalData;
  initialTab?: string;
}

export const EditMedical: React.FC<EditMedicalProps> = ({
  isOpen,
  onClose,
  refetch,
  programNumber,
  medicalData,
  initialTab = "diagnosis"
}) => {
  const [formData, setFormData] = useState<FormData>({
    icd10Codes: [],
    psychotropicMeds: "",
    otherMeds: "",
    clinicalPresentation: "",
    psychHistory: "",
    medicalProblems: "",
    substanceHistory: "",
    dsm5dx: "",
    currentMeds: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update active tab when initialTab changes or modal opens
  useEffect(() => {
      if (isOpen) {
          setActiveTab(initialTab);
      }
  }, [isOpen, initialTab]);

  const { data: icd10Data, loading: icd10Loading, error: icd10Error } = useQuery(GET_ICD10_CODES);
  const [updateParticipant] = useMutation(UPDATE_PARTICIPANT);

  const icd10Options: ICD10Option[] = React.useMemo(() => {
    if (!icd10Data?.getICD10Codes) return [];
    return icd10Data.getICD10Codes.map((code: { icd10: string; icd10Description: string }) => ({
      value: code.icd10,
      label: `${code.icd10} - ${code.icd10Description}`,
    }));
  }, [icd10Data]);

  useEffect(() => {
    if (medicalData) {
      setFormData({
        icd10Codes: medicalData.icd10
          ? medicalData.icd10.split(',').map(
              (code) =>
                icd10Options.find((option) => option.value === code.trim()) || {
                  value: code.trim(),
                  label: code.trim(),
                }
            )
          : [],
        psychotropicMeds: medicalData.psychotropicMeds || "",
        otherMeds: medicalData.otherMeds || "",
        clinicalPresentation: medicalData.clinicalPresentation || "",
        psychHistory: medicalData.psychHistory || "",
        medicalProblems: medicalData.medicalProblems || "",
        substanceHistory: medicalData.substanceHistory || "",
        dsm5dx: medicalData.dsm5dx || "",
        currentMeds: medicalData.currentMeds || "",
      });
    }
  }, [medicalData, icd10Options]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleIcd10Change = (selectedOptions: readonly ICD10Option[] | null) => {
    setFormData((prev) => ({ 
      ...prev, 
      icd10Codes: selectedOptions ? Array.from(selectedOptions) : [] 
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await updateParticipant({
        variables: {
          odrnumber: programNumber,
          updates: {
            psychotropicMeds: formData.psychotropicMeds,
            otherMeds: formData.otherMeds,
            clinicalPresentation: formData.clinicalPresentation,
            psychHistory: formData.psychHistory,
            medicalProblems: formData.medicalProblems,
            substanceHistory: formData.substanceHistory,
            dsm5dx: formData.dsm5dx,
            currentMeds: formData.currentMeds,
            icd10: formData.icd10Codes.map(code => code.value).join(','),
          },
        },
      });
      await refetch();
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred while updating medical data");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (icd10Loading) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b bg-white flex flex-row items-center justify-between shrink-0">
          <DialogTitle className="text-xl font-bold text-gray-800">Edit Medical Information</DialogTitle>
        </DialogHeader>

        {error && (
          <div className="px-6 pt-4 bg-white shrink-0">
             <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
             </Alert>
          </div>
        )}

        <form id="edit-medical-form" onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 bg-gray-50/30">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
             <div className="px-6 py-2 border-b bg-gray-50 shrink-0">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="diagnosis" className="flex items-center gap-2">
                     <Activity className="h-4 w-4" /> Diagnosis
                  </TabsTrigger>
                  <TabsTrigger value="medications" className="flex items-center gap-2">
                     <Pill className="h-4 w-4" /> Medications
                  </TabsTrigger>
                  <TabsTrigger value="history" className="flex items-center gap-2">
                     <History className="h-4 w-4" /> History
                  </TabsTrigger>
                </TabsList>
             </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
                <TabsContent value="diagnosis" className="space-y-6 mt-0">
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="icd10" className="text-xs font-bold uppercase text-gray-500">ICD-10 Codes</Label>
                            <Select
                            isMulti
                            id="icd10"
                            name="icd10Codes"
                            options={icd10Options}
                            value={formData.icd10Codes}
                            onChange={handleIcd10Change}
                            placeholder="Search ICD-10..."
                            className="w-full"
                            isDisabled={isSubmitting}
                            classNames={{
                                control: () => "!bg-white !border-gray-200",
                                input: () => "!text-sm",
                                option: () => "!text-sm",
                            }}
                            />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="dsm5dx" className="text-xs font-bold uppercase text-gray-500">DSM-5 Diagnosis</Label>
                                <Textarea
                                    id="dsm5dx"
                                    name="dsm5dx"
                                    value={formData.dsm5dx}
                                    onChange={handleInputChange}
                                    className="min-h-[100px] text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="medicalProblems" className="text-xs font-bold uppercase text-gray-500">Medical Problems</Label>
                                <Textarea
                                    id="medicalProblems"
                                    name="medicalProblems"
                                    value={formData.medicalProblems}
                                    onChange={handleInputChange}
                                    className="min-h-[100px] text-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="clinicalPresentation" className="text-xs font-bold uppercase text-gray-500">Clinical Presentation</Label>
                            <Textarea
                                id="clinicalPresentation"
                                name="clinicalPresentation"
                                value={formData.clinicalPresentation}
                                onChange={handleInputChange}
                                className="min-h-[120px] text-sm"
                            />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="medications" className="space-y-6 mt-0">
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="psychotropicMeds" className="text-xs font-bold uppercase text-gray-500">Psychotropic Medications</Label>
                            <Textarea
                                id="psychotropicMeds"
                                name="psychotropicMeds"
                                value={formData.psychotropicMeds}
                                onChange={handleInputChange}
                                className="min-h-[100px] text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="currentMeds" className="text-xs font-bold uppercase text-gray-500">Current Medications</Label>
                            <Textarea
                                id="currentMeds"
                                name="currentMeds"
                                value={formData.currentMeds}
                                onChange={handleInputChange}
                                className="min-h-[100px] text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="otherMeds" className="text-xs font-bold uppercase text-gray-500">Other Medications</Label>
                            <Textarea
                                id="otherMeds"
                                name="otherMeds"
                                value={formData.otherMeds}
                                onChange={handleInputChange}
                                className="min-h-[100px] text-sm"
                            />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="history" className="space-y-6 mt-0">
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="psychHistory" className="text-xs font-bold uppercase text-gray-500">Psychiatric History</Label>
                            <Textarea
                                id="psychHistory"
                                name="psychHistory"
                                value={formData.psychHistory}
                                onChange={handleInputChange}
                                className="min-h-[150px] text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="substanceHistory" className="text-xs font-bold uppercase text-gray-500">Substance Use History</Label>
                            <Textarea
                                id="substanceHistory"
                                name="substanceHistory"
                                value={formData.substanceHistory}
                                onChange={handleInputChange}
                                className="min-h-[150px] text-sm"
                            />
                        </div>
                    </div>
                </TabsContent>
            </div>
          </Tabs>
        </form>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center shrink-0 rounded-b-lg">
          <Button 
            type="submit"
            form="edit-medical-form"
            size="sm"
            disabled={isSubmitting}
            className="gap-1.5 px-6 shadow-sm"
          >
            <Save className="h-4 w-4" />
            {isSubmitting ? "Saving Changes..." : "Save Changes"}
          </Button>
          <Button 
            variant="outline"
            type="button"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditMedical;
